import {useCallback, useEffect, useState} from 'react'

/**
 * Hook for detecting text overflow in reference token elements
 *
 * @param selectedReferences - Array of references to monitor for changes
 * @param tokenElementsRef - Ref to array of token DOM elements
 * @param containerRef - Ref to the container element for ResizeObserver
 * @param textElementSelector - CSS selector for the text elements to check for overflow
 * @returns boolean indicating whether any text is overflowing
 */
export function useOverflowDetection<T>(
  selectedReferences: T[],
  tokenElementsRef: React.RefObject<Array<HTMLElement | null>>,
  containerRef: React.RefObject<HTMLElement>,
  textElementSelector: string,
): boolean {
  const [hasOverflowingText, setHasOverflowingText] = useState(false)

  // Check if any reference names are overflowing
  const checkForOverflow = useCallback(() => {
    if (!tokenElementsRef.current) return

    const hasOverflow = tokenElementsRef.current.some(token => {
      if (!token) return false

      const textElement = token.querySelector(textElementSelector)
      if (!textElement) return false

      return textElement.scrollWidth > textElement.clientWidth
    })

    setHasOverflowingText(hasOverflow)
  }, [tokenElementsRef, textElementSelector])

  // Check for overflow when references change or component layout changes
  useEffect(() => {
    if (selectedReferences.length === 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setHasOverflowingText(false)
      return
    }

    const frameId = requestAnimationFrame(checkForOverflow)
    return () => cancelAnimationFrame(frameId)
  }, [selectedReferences, checkForOverflow])

  // Also check for overflow on container resize
  useEffect(() => {
    if (selectedReferences.length === 0 || !containerRef.current) return

    const resizeObserver = new ResizeObserver(() => {
      checkForOverflow()
    })

    resizeObserver.observe(containerRef.current)

    return () => {
      resizeObserver.disconnect()
    }
  }, [selectedReferences.length, checkForOverflow, containerRef])

  return hasOverflowingText
}
